<template>
  <ApplicationBase
    v-bind="vBindBase"
    color="green"
    applicationType="aso"
    applicationTypeOriginal="right-of-occupancy"
    v-on="vOnBase"
    class="application-right-of-occupancy"
  >
    <template #top>

      <!-- Asumisoikeuden järjestysnumero -->

      <ApplicationSection
        wrapperClasses="container container--narrow application-section-bg"
        :title="translate('Asumisoikeuden järjestysnumero')"
        >

        <div
          v-if="textOrderNumberBefore"
          class="wysiwyg print-hide text-ordernumber-before"
          v-html="textOrderNumberBefore"
        />
        <div
          v-if="warningOrderNumberBefore"
          class="wysiwyg warning-text print-hide border--green text-ordernumber-before"
          v-html="warningOrderNumberBefore"
        />

        <div v-if="checkTime()"
          class="print-hide text-ordernumber-before">

          {{translate('Järjestysnumeroni on:')}}
          <div role="radiogroup" class="order-number-radio-group">
            <label>
              <input type="radio" name="radio" :value="false" v-model="form.isARA" />
              <span>{{translate('Kuntakohtainen')}}</span>
            </label>
            <label>
              <input
                type="radio"
                name="radio"
                :value="true"
                v-model="form.isARA"
              /><span>{{translate('Valtakunnallinen ARA-numero')}}</span>
            </label>
          </div>
        </div>

        <div class="application-right-of-occupancy__order-number-row row">
          <div class="col-md-6">
            <!-- Show spinner if apartments loading OR v$ not ready -->
            <LoadSpinner v-if="loadingApartments || !isVuelidateReady"/>

            <div
              class="form"
              v-else
            >
              <div class="form__grid">

                <div
                  v-if="form.isARA && v$ && v$.form"
                  class="form__col"
                >
                <InputWrap
                    :label="translate('ARAn järjestysnumero')"
                    :info="null"
                    :required="form.isARA && (!residentInLocation.isResident || numberOfGroupedLocalities > 1)"
                    :validations="v$.form.ASOAraOrderNumber"
                    :validation-messages="{
                      required: translate('Järjestysnumero on pakollinen.'),
                      maxLength: translate('Järjestysnumeron maksimipituus on enintään 6 merkkiä.')
                    }"
                    v-slot="{error, touch}"

                  >
                    <InputText
                      v-model="v$.form.ASOAraOrderNumber.$model"
                      data-cy="aso-ara-ordernumber-input"
                      :error="error"
                      @blur="touch"
                    />
                  </InputWrap>
                </div>

                <div v-if="!form.isARA && v$ && v$.orderNumbers">
                  <div
                    v-for="(city, index) in cities"
                    :key="city.uuid"
                    class="form__col"
                  >
                    <InputWrap
                      :label="sprintf(translate('Järjestysnumero (%s)'), city.label)"
                      v-slot="{error, touch}"
                      :validations="preview ? null : (v$.orderNumbers[city.uuid] || null)"
                      :required="!form.isARA && resolveASOOrderNumberRequired(city, index, numberOfGroupedLocalitiesByCity)"
                    >
                      <InputText
                        v-model="v$.orderNumbers[shareOrderNumber && index > 0 ? cities[0].uuid : city.uuid].$model"
                        :disabled="preview || shareOrderNumber && index > 0"
                        :error="error"
                        data-cy="aso-ordernumber-input"
                        @blur="touch"
                      />
                    </InputWrap>

                    <InputCheckbox
                      v-if="!preview && index === 0 && cities.length > 1"
                      v-model="shareOrderNumber"
                      class="application-right-of-occupancy__share-checkbox print-hide"
                    >
                      {{translate('Käytä samaa numeroa kaikkiin kohteisiin')}}
                    </InputCheckbox>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

        <div
          class="wysiwyg print-hide"
          v-html="textOrderNumberAfter"
        />
      </ApplicationSection>
    </template>

    <template #apartments>
      <ApplicationApartments
        v-bind="vBindApartments"
        color="green"
        v-on="vOnApartments"
        apartmentTypeSearchQueryValue="right-of-occupancy"
      />
    </template>

    <!-- Only render applicants section when vuelidate is ready -->
    <div class="container container--narrow mb-ts-2" v-if="canRenderForm">

      <div class="application-section-bg">
        <ApplicationApplicants
          v-bind="vBindApplicants"
          @update="applicants = $event"
          applicationType="right-of-occupancy"
        />
      </div>
    </div>
    <div class="container container--narrow mb-ts-2" v-else>
      <LoadSpinner />
    </div>

    <ApplicationOtherResidents
      v-if="canRenderForm && form.OtherResidents"
      :items="form.OtherResidents || []"
      :item-init="createOtherResident"
      :v-iter="vFormOtherResidentsIter"
      :preview="preview"
      @update="form.OtherResidents = $event"
    />

    <!-- Only render current housing info section when v$ is ready -->
    <div class="container container--narrow" v-if="v$ && v$.form">
      <div class="application-section-bg mb-ts-2">
          <ApplicationSection
            :title="translate( 'Tiedot nykyisestä asunnosta' )"
            wrapper-classes="background-color-green-lighter container--with-background form--white-fields"
          >
          <div class="form">
            <div class="form__grid">
              <div class="form__row">
                <InputWrap
                  class="form__col-6"
                  :label="translate('Nykyinen asumismuoto')"
                  :info="null"
                  :required="true"
                  :validations="v$.form.ARACurrentAccommodationType"
                  :validation-messages="{required: translate('Nykyinen asumismuoto vaaditaan.')}"
                  data-cy="ara-current-accommodation-type"
                  v-slot="{error, touch}"
                >
                  <InputSelect
                    v-model="form.ARACurrentAccommodationType"
                    :error="error"
                    :allowEmpty="false"
                    :options="[
                      {label: translate('Omistusasunto'), value: '1'},
                      {label: translate('Asumisoikeusasunto'), value: '2'},
                      {label: translate('Vuokra-asunto'), value: '3'},
                      {label: translate('Muu'), value: '4'},
                    ]"
                    simple-value
                    :searchable="false"
                    @close="touch"
                  />
                </InputWrap>
                <InputWrap
                  class="form__col-6"
                  :label="translate('Kunta, josta muuttaa')"
                  :info="null"
                  :required="true"
                  :validations="v$.form.ARACurrentMunicipality"
                  :validation-messages="{required: translate('Kunta, josta muuttaa vaaditaan.')}"
                  data-cy="ara-current-municipality"
                  v-slot="{error, touch}"
                >
                  <InputSelect
                    ref="municipalitiesSelect"
                    v-model="form.ARACurrentMunicipality"
                    :error="error"
                    :allow-empty="false"
                    :close-on-select="true"
                    :multiple="false"
                    :prevent-autofocus="true"
                    :searchable="false"
                    :options="[
                      ...municipalities // imported from municipalities.js
                    ]"
                    simple-value
                    track-by=value
                    label=label
                    @close="touch"
                  />
                </InputWrap>
              </div>
            </div>
          </div>

          </ApplicationSection>
      </div>
    </div>
    <div class="container container--narrow" v-else>
      <LoadSpinner />
    </div>

    <input type="hidden" name="Checksum" :value=form.Checksum />

  </ApplicationBase>
</template>

<script>
  import {required, requiredIf, integer, maxLength, helpers} from '@vuelidate/validators';
  import {sprintf} from 'sprintf-js';

  import {validations} from '../lib/applicationShared';
  import application from './mixins/application';
  import LoadSpinner from './components/LoadSpinner';
  import globals from '../lib/globals';

  import { municipalities } from '../helpers/municipalities'

  export default {
    components: {
      LoadSpinner,
    },
    mixins: [
      application({
        favouritesType: 'right-of-occupancy',
        presetMapExtend: {
          //
        },
        localSaveExtend: {
          localSaveFromState(state) {
            return {
              shareOrderNumber: state.shareOrderNumber,
              orderNumbers: state.orderNumbers,
              ASOAraOrderNumber: state.ASOAraOrderNumber,
            };
          },
          stateFromLocalSave(state, localSave) {
            state.shareOrderNumber = localSave.shareOrderNumber;
            state.orderNumbers = localSave.orderNumbers;
            state.ASOAraOrderNumber = localSave.ASOAraOrderNumber;
            //state.isARA = localSave.isARA;
          },
        },
        applyOtherData: {
          conditionFavouritesType: 'rental',
          title: 'Korissasi on myös vuokra-asuntoja',
          ctaText: 'Jätä vuokra-asuntohakemus',
          ctaUrl: globals.urlApplicationRent,
          color: 'green',
        },
      }),
    ],
    props: {
      textOrderNumberAfter: {
        type: String,
        default: '',
      },
      textOrderNumberBefore: {
        type: String,
        default: '',
      },
      warningOrderNumberBefore: {
        type: String,
        default: '',
      },
    },
    data() {
      return {
        shareOrderNumber: false,
        orderNumbers: {},
        // Component data() replaces the mixin `form` object entirely (Vue shallow merge);
        // include the same base keys as mixins/application.js so OtherResidents and ARA fields exist.
        form: {
          Id: null,
          OtherResidents: [],
          AdditionalWishes: '',
          ASOAraOrderNumber: '',
          isARA: false,
          ARACurrentAccommodationType: '',
          ARACurrentMunicipality: '',
          Checksum: '',
          Type: '1',
          TotalWealth: '',
          ApplicationReason: null,
          NeedForApartmentStartDate: null,
          ApplicationReasonAdditionalInfo: '',
          CurrentApartmentInfo: {
            AccommodationType: null,
            ApartmentType: null,
            Area: '',
            NumberOfResidents: '',
            Rent: '',
            MaintenanceCharge: '',
          },
        },
        municipalities,
      };
    },
    computed: {
      cities() {
        const objectCities = Object.assign({}, ...this.apartments
          .map(apartment => ({[apartment.city.mpc_kuntaid]: apartment.city.mpc_name})));
        return Object.entries(objectCities).map(([uuid, label]) => ({uuid, label}));
      },
    },
    validations() {
      const validationsOrderNumber = {
        integer
      };
      const validationsOrderNumberRequired = {
        integer,
        required
      };

      // The data in orderNumbers also includes cities that might have been removed since. We must only validate the
      // ones that are still "active". Also, when using shared order number, we should only validate the first item.
      // KS 09/2023 - order numbers are required under some conditions -> need to use resolver to figure that out
      // The resolver function is a copy of resolveASOOrderNumberRequired in methods (TODO: find a way to combine them)
      const cityUuids = this.cities.map(city => city.uuid);
      function shouldValidateOrderNumber(shareOrderNumber, cities, uuid, numberOfGroupedLocalitiesByCity, form, residentInLocation) {
        const resolveRes = resolveASOOrderNumberRequiredForValidation(uuid, cities, numberOfGroupedLocalitiesByCity, form, shareOrderNumber, residentInLocation);
        return shareOrderNumber
          ? (cities.length > 0 ? resolveRes : false)
          : (cityUuids.includes(uuid) && resolveRes);
      };
      function resolveASOOrderNumberRequiredForValidation(city, cities, numberOfGroupedLocalitiesByCity, form, shareOrderNumber, residentInLocation) {
        let isRequired = false;
        // If ARA-number is not selected isRequired=true by default
        if (!form.isARA) {
          isRequired = true;
        } else {
          // ARA-number selected -> no city order number required
          return false;
        }
        // If there are more than one city
        if (Object.keys(numberOfGroupedLocalitiesByCity).length > 1) {
          // If the ASO number is shared
          if (shareOrderNumber) {
            // Only first number is required
            if (cities[0].uuid === city) {
              isRequired = true;
            } else {
              isRequired = false;
            }
          } else {
            // Are there multiple localities in the current city
            if (numberOfGroupedLocalitiesByCity[city] && numberOfGroupedLocalitiesByCity[city].length > 1) {
              isRequired = true;
            // Only one locality
            } else {
              // Are we resident in the locality
              if (numberOfGroupedLocalitiesByCity[city] && residentInLocation.localityId === numberOfGroupedLocalitiesByCity[city][0]) {
                isRequired = false;
              } else {
                isRequired = true;
              }
            }
          }
        // Only one city
        } else {
          // If there are more than one locality in the city
          if (numberOfGroupedLocalitiesByCity[city] && numberOfGroupedLocalitiesByCity[city].length > 1) {
            isRequired = true;
          // Only one locality
          } else {
            // Check if isResident in the locality
            if (numberOfGroupedLocalitiesByCity[city] && residentInLocation.localityId === numberOfGroupedLocalitiesByCity[city][0]) {
              isRequired = false;
            } else {
              isRequired = true;
            }
          }
        }
        return isRequired;
      };

      // Validations.
      return {
        orderNumbers: Object.assign({}, ...Object
          .entries(this.orderNumbers)
          .map(([key]) => ({[key]:
            shouldValidateOrderNumber(this.shareOrderNumber, this.cities, key, this.numberOfGroupedLocalitiesByCity, this.form, this.residentInLocation)
              ? validationsOrderNumberRequired : validationsOrderNumber}))
        ),
        applicants: {
          $each: helpers.forEach({
            ...validations.applicant,
          }),
        },
        form: {
          ...validations.form,
          ASOAraOrderNumber: {
            required: requiredIf(function () {
              return this.form.isARA && (!this.residentInLocation.isResident || this.numberOfGroupedLocalities > 1)
            }),
            integer,
            maxLength: maxLength(6)
          },
          ARACurrentMunicipality: {
            required
          },
          ARACurrentAccommodationType: {
            required
          },
        }
      };
    },
    watch: {
      cities() {
        // Whenever cities are added, orderNumbers must be updated with new properties.
        this.orderNumbers = {
          ...Object.assign({}, ...this.cities.map(city => ({[city.uuid]: ''}))),
          ...this.orderNumbers,
        };
      },
    },
    methods: {
      sprintf,

      checkTime: function() {
        // Are we past the time (29.12.2023 10:00:00) when we should hide kuntakohtainen aso-ordernumber?
        if (Date.now() >= 1703836800000) {
          this.form.isARA = true;
          return false;
        } else {
          return true;
        }
      },
      resolveASOOrderNumberRequired(city, index, numberOfGroupedLocalitiesByCity) {
        let isRequired = false;
        // If ARA-number is not selected isRequired=true by default
        if (!this.form.isARA) {
          isRequired = true;
        } else {
          // ARA-number has been selected -> city order number is not required
          return false;
        }
        // If there are more than one city
        if (Object.keys(numberOfGroupedLocalitiesByCity).length > 1) {
          // If the ASO number is shared
          if (this.shareOrderNumber) {
            // Only first number is required
            if (index > 0) {
              isRequired = false;
            } else {
              isRequired = true;
            }
          } else {
            // Are there multiple localities in the current city
            if (numberOfGroupedLocalitiesByCity[city.uuid] && numberOfGroupedLocalitiesByCity[city.uuid].length > 1) {
              isRequired = true;
            // Only one locality
            } else {
              // Are we resident in the locality
              if (numberOfGroupedLocalitiesByCity[city.uuid] && this.residentInLocation.localityId === numberOfGroupedLocalitiesByCity[city.uuid][0]) {
                isRequired = false;
              } else {
                isRequired = true;
              }
            }
          }
        // Only one city
        } else {
          // If there are more than one locality in the city
          if (numberOfGroupedLocalitiesByCity[city.uuid] && numberOfGroupedLocalitiesByCity[city.uuid].length > 1) {
            isRequired = true;
          // Only one locality
          } else {
            // Check if isResident in the locality
            if (numberOfGroupedLocalitiesByCity[city.uuid] && this.residentInLocation.localityId === numberOfGroupedLocalitiesByCity[city.uuid][0]) {
              isRequired = false;
            } else {
              isRequired = true;
            }
          }
        }
        return isRequired;
      },

      createFormApartment(apartment) {
        const cityId = this.shareOrderNumber
          ? this.cities[0].uuid
          : apartment.city.mpc_kuntaid;
        return {
          Id: apartment.mpc_asuntoid,
          // Set ASOOrderNumber to city order nr if there is no ARA-number
          // otherwise fill with ARA-number (unless the applicant is resident in the locality)
          ASOOrderNumber:
            !this.form.isARA
              ? (apartment.mpc_kohdeid !== this.residentInLocation.localityId) ? this.orderNumbers[cityId] : ''
              : (this.form.isARA && apartment.mpc_kohdeid !== this.residentInLocation.localityId) ? this.form.ASOAraOrderNumber : '',
          Resident: apartment.mpc_kohdeid === this.residentInLocation.localityId ? 'true' : 'false',
          CampaignCode: null,
        };
      },

      isExisting() {
        return (
          this.isExistingApplication
        )
      },
    },
  }
</script>

<style lang="scss">
  @import '~styles/definitions';

  .application-right-of-occupancy {
  }

  .application-right-of-occupancy__order-number-row {
    margin-bottom: r(32);
  }

  .application-right-of-occupancy__share-checkbox {
    margin-top: r(4);
  }

  .text-ordernumber-before {
    margin-bottom: r(32);
  }

  .order-number-radio-group {
    display: flex;
    flex-direction: column;

    label {
      margin: r(4) 0 0;

      span {
        margin-left: r(4);
      }
    }
  }
</style>
